import { ComponentType } from 'react'
import { Pressable, View } from 'react-native'
import { DS } from '@/design-system'

import Card from '@/components/Mini/Card'
import RowCard, { RowCardBadge } from '@/components/Mini/RowCard'
import Section from '@/components/Mini/Section'
import { Badge } from '@/screens/play-screen/Sections/TodayLiveGames/Badge'

import CthImage from '@/screens/play-screen/Sections/MiniGames/assets/completeTheHand.svg'
import Daily10Image from '@/screens/play-screen/Sections/MiniGames/assets/daily10.svg'
import PocketPickImage from '@/screens/play-screen/Sections/MiniGames/assets/pocketPick.svg'
import CthThumb from '@/screens/play-screen/Sections/MiniGames/assets/completeTheHandThumb.svg'
import Daily10Thumb from '@/screens/play-screen/Sections/MiniGames/assets/daily10Thumb.svg'
import PocketPickThumb from '@/screens/play-screen/Sections/MiniGames/assets/pocketPickThumb.svg'

import { GAME_CONSTANTS } from '@ppwr/convex/shared/constants'
import { events } from '@/models/analytics/analytics-events'
import * as pocketPick from '@/router/(root)/pocketPick/pocketPickModule'
import * as d10 from '@/router/(root)/daily10/d10Module'
import * as cth from '@/router/(root)/cth/cthModule'

type MiniGameStatus =
  | {
      status: 'available' | 'in_progress' | 'completed'
    }
  | null
  | undefined

type MiniGameCardProps = {
  isNew?: boolean
  label: string
  status: MiniGameStatus
  GameImage: ComponentType
  handleModule: () => void
}

const DESCRIPTIONS = {
  pocketPick: 'Choose the strongest second card and see how your starting hand stacks up.',
  cth: '2 cards are on the table. Pick 3 more and build the strongest hand.',
  daily10: 'Start with 10,000 chips and see how much you can win in 10 hands!'
}

function MiniGames() {
  return (
    <Section title="Today's Challenges">
      <PocketPickMiniGameRow />
      <CthMiniGameRow />
      <Daily10MiniGameRow />
    </Section>
  )
}

function usePocketPickMiniGame() {
  const { status } = pocketPick.usePocketPickQueryStatus()
  const { GAME_NAME } = GAME_CONSTANTS.POCKET_PICK_CONSTANTS
  const handleModule = pocketPick.useHandleModule({ event: events.pocketPick.play_screen_card_tapped })
  return { label: GAME_NAME, status, handleModule }
}

function useCthMiniGame() {
  const { status } = cth.useCthQueryStatus()
  const { GAME_NAME } = GAME_CONSTANTS.CTH_CONSTANTS
  const handleModule = cth.useHandleModule({ event: events.cth.play_screen_card_tapped })
  return { label: GAME_NAME, status, handleModule }
}

function useDaily10MiniGame() {
  const { status } = d10.useD10QueryStatus()
  const { DEFAULT_GAME_NAME } = GAME_CONSTANTS.DAILY10_CONSTANTS
  const handleModule = d10.useHandleModule({ event: events.daily10.play_screen_card_tapped })
  return { label: DEFAULT_GAME_NAME, status, handleModule }
}

/** Shared badge rules: "New" (orange) for new games, "Played" (dark grey) once completed. */
function getMiniGameBadge(isNew: boolean, status: MiniGameStatus): RowCardBadge | undefined {
  if (status?.status === 'completed') {
    return { label: 'Played', icon: 'Check', backgroundColor: DS.palette['Brand/Dark Grey'] }
  }
  if (isNew) {
    return { label: 'New', backgroundColor: DS.palette['Brand/Orange'] }
  }
  return undefined
}

// Row layout (home redesign)

function PocketPickMiniGameRow() {
  const { label, status, handleModule } = usePocketPickMiniGame()
  return (
    <RowCard
      title={label}
      description={DESCRIPTIONS.pocketPick}
      Thumbnail={PocketPickThumb}
      badge={getMiniGameBadge(false, status)}
      onPress={handleModule}
    />
  )
}

function CthMiniGameRow() {
  const { label, status, handleModule } = useCthMiniGame()
  return (
    <RowCard
      title={label}
      description={DESCRIPTIONS.cth}
      Thumbnail={CthThumb}
      badge={getMiniGameBadge(false, status)}
      onPress={handleModule}
    />
  )
}

function Daily10MiniGameRow() {
  const { label, status, handleModule } = useDaily10MiniGame()
  return (
    <RowCard
      title={label}
      description={DESCRIPTIONS.daily10}
      Thumbnail={Daily10Thumb}
      badge={getMiniGameBadge(false, status)}
      onPress={handleModule}
    />
  )
}

// Tile layout (previous home design, kept for reuse)

function MiniGamesGrid() {
  return (
    <View>
      <DS.Text variant="Heading 3/Medium" style={{ paddingBottom: 12 }}>
        Today's Challenges
      </DS.Text>
      <View style={{ flexDirection: 'row', gap: 12 }}>
        <PocketPickMiniGameCard />
        <CthMiniGameCard />
        <Daily10MiniGameCard />
      </View>
    </View>
  )
}

function PocketPickMiniGameCard() {
  const { label, status, handleModule } = usePocketPickMiniGame()
  return <MiniGameCard label={label} status={status} GameImage={PocketPickImage} handleModule={handleModule} />
}

function CthMiniGameCard() {
  const { label, status, handleModule } = useCthMiniGame()
  return <MiniGameCard label={label} status={status} GameImage={CthImage} handleModule={handleModule} />
}

function Daily10MiniGameCard() {
  const { label, status, handleModule } = useDaily10MiniGame()
  return <MiniGameCard label={label} status={status} GameImage={Daily10Image} handleModule={handleModule} />
}

function MiniGameCard({ isNew = false, label, status, GameImage, handleModule }: MiniGameCardProps) {
  const badge = getMiniGameBadge(isNew, status)

  return (
    <Card style={{ padding: 12 }}>
      <Pressable style={{ flex: 1 }} onPress={handleModule}>
        {badge && (
          <View style={{ position: 'absolute', alignSelf: 'center', zIndex: 100, top: -12 }}>
            <Badge label={badge.label} backgroundColor={badge.backgroundColor} icon={badge.icon} />
          </View>
        )}

        <View style={{ gap: 12, maxWidth: 83 }}>
          <GameImage />
          <DS.Text variant="Heading 6/Medium" style={{ textAlign: 'center' }}>
            {label}
          </DS.Text>
        </View>
      </Pressable>
    </Card>
  )
}

export { MiniGames, MiniGamesGrid, MiniGameCard }
